<template>
    <AppLoadingContainer :predicate="!isLoaded">
        <form @submit.prevent="onSubmit()">
            <div class="card-body">
                <div class="row">
                    <div class="col-lg-6 col-md-12 form-group">
                        <label for="buildingName">Office Name</label>
                        <input :class="{'is-invalid': $v.building.name.$invalid}" autocomplete="off"
                                class="form-control"
                                maxlength="70"
                                placeholder="Office Name"
                                v-model.lazy="building.name"/>
                        <span class="invalid-feedback"
                              v-if="!$v.building.name.required">Field is required</span>
                        <span class="invalid-feedback" v-if="!$v.building.name.isUnique">The building name is already in use</span>
                        <span class="invalid-feedback" v-if="!$v.building.name.maxLength">Name must be &le; 70 characters</span>
                    </div>
                    <div class="col-lg-3 col-md-6 col-sm-12 form-group">
                        <label for="shortName">Office Short Name</label>
                        <input :class="{'is-invalid': $v.building.shortName.$invalid}" class="form-control"
                                maxlength="15"
                                placeholder="Office Short Name" v-model="building.shortName"/>
                        <span class="invalid-feedback" v-if="!$v.building.shortName.maxLength">Short name must be &le; 15 characters</span>
                    </div>
                    <div class="col-lg-3 col-md-6 col-sm-12 form-group">
                        <label for="code">TAMU Building Code</label>
                        <input class="form-control" disabled
                                maxlength="15" placeholder="TAMU Building Code" v-model.lazy="building.code"/>
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-12 form-group">
                        <label for="address">Address</label>
                        <input :class="{'is-invalid': $v.building.address.$invalid}" class="form-control"
                                maxlength="100"
                                placeholder="Address" v-model="building.address"/>
                        <span class="invalid-feedback"
                              v-if="!$v.building.address.required">Field is required</span>
                        <span class="invalid-feedback" v-if="!$v.building.address.maxLength">Address must be &le; 100 characters</span>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-3 col-lg-4 form-group">
                        <label for="City">City</label>
                        <input :class="{'is-invalid': $v.building.city.$invalid}" class="form-control"
                                maxlength="50"
                                placeholder="City" v-model="building.city"/>
                        <span class="invalid-feedback"
                              v-if="!$v.building.city.required">Field is required</span>
                        <span class="invalid-feedback" v-if="!$v.building.city.maxLength">City must be &le; 50 characters</span>
                    </div>

                    <div class="col-md-3 col-lg-4 form-group">
                        <label for="City">State</label>
                        <AppSelectState v-model="building.stateCode"/>
                    </div>

                    <div class="col-md-3 col-lg-4  form-group">
                        <label for="zipCode">Zip Code</label>
                        <input :class="{'is-invalid': $v.building.zipCode.$invalid}" class="form-control"
                                maxlength="12"
                                placeholder="Zip Code" v-model="building.zipCode"/>
                        <span class="invalid-feedback"
                              v-if="!$v.building.zipCode.required">Field is required</span>
                        <span class="invalid-feedback" v-if="!$v.building.zipCode.maxLength">Zip code must be &le; 12 characters</span>
                    </div>

                    <div class="col-md-3 col-lg-4  form-group">
                        <label for="mailStop">Mail Stop</label>
                        <input :class="{'is-invalid': $v.building.mailStop.$invalid}" class="form-control"
                                maxlength="15"
                                placeholder="Mail Stop" v-model="building.mailStop"/>
                        <span class="invalid-feedback" v-if="!$v.building.city.maxLength">Mail stop must be &le; 15 characters</span>
                    </div>
                </div>
            </div>
            <div class="card-footer d-print-none text-right">
                <button :disabled="isSubmitting" class="btn btn-primary" type="submit">Save</button>
                <RouterLink :to="{name:'Buildings'}" class="btn btn-danger">Back</RouterLink>
            </div>
        </form>
    </AppLoadingContainer>
</template>

<script>
    import {ToastType} from "@/services/models/shared/ToastType";
    import {maxLength, required} from "vuelidate/lib/validators";
    import AppSelectState from "@/components/shared/AppSelectState";
    import AddressService from "@/services/AddressService";
    import AppLoadingContainer from "@/components/shared/AppLoadingContainer";

    export default {
        components: {
            AppSelectState,
            AppLoadingContainer
        },
        data() {
            return {
                building: {
                    id: null,
                    name: null,
                    code: null,
                    shortName: null,
                    city: null,
                    stateCode: null,
                    zipCode: null,
                    mailStop: null,
                    address: null
                },
                isSubmitting: false,
                isLoaded: false
            }
        },
        props: {
            buildingId: {
                type: String,
                required: true
            }
        },
        methods: {
            /**
             * @description Updates the building and redirects if successful
             */
            onSubmit() {
                const vm = this;

                // stop here if form is invalid
                this.$v.$touch();
                if (this.$v.$invalid) {
                    return;
                }

                vm.isSubmitting = true;
                AddressService.updateBuilding(vm.building).then(() => {
                    vm.alert(ToastType.Success, `The office '${vm.building.name}' was successfully updated.`);

                    // Redirect to the buildings page
                    vm.$router.push({name: 'Buildings'})
                }).catch(function (error) {
                    vm.alert(ToastType.Danger, error.response.statusText)
                }).finally(() => {
                    vm.isSubmitting = false;
                });
            },
        },
        created() {
            const vm = this;

            // Retrieve this specific general code
            AddressService.getBuilding(this.buildingId)
                .then(function (response) {
                    vm.building = response;
                    vm.building.address = response.address1
                }).catch(() => {
                vm.redirectToError()
            }).finally(() => vm.isLoaded = true);
        },
        validations: {
            building: {
                name: {
                    required,
                    maxLength: maxLength(70),
                    isUnique(value) {
                        if (!value) return true;

                        const vm = this;

                        return AddressService.getBuildingList({
                            page: 1,
                            pageSize: 1,
                            name: value
                        }).then(response => response.totalCount === 0 || response.data.indexOf(i => i.id === vm.building.id && value.toUpperCase() === vm.building.name.toUpperCase()) === -1)
                            .catch(() => {
                                return true;
                            })
                    }
                },
                shortName: {
                    maxLength: maxLength(15)
                },
                address: {
                    required,
                    maxLength: maxLength(100)
                },
                city: {
                    required,
                    maxLength: maxLength(50)
                },
                zipCode: {
                    required,
                    maxLength: maxLength(12)
                },
                mailStop: {
                    maxLength: maxLength(15)
                }
            }
        }
    }
</script>
